import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutlined";
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	FormControlLabel,
	FormGroup,
	IconButton,
	InputAdornment,
	Tooltip,
	Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { type ChangeEvent, type FC, useCallback, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useRecommendationsContext } from "@/contexts/Recommendations/RecommendationsContext";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { useOptionalWeekViewContext } from "@/contexts/WeekView";
import { useAgencyAvailability } from "@/hooks/useAgencyAvailability";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { useIncentiveConfigurations } from "@/hooks/useIncentiveConfigurations";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useScheduleWeeks } from "@/hooks/useScheduleWeeks";
import { useShiftTimes } from "@/hooks/useShiftTimes";
import { STAFF_PICKER } from "@/theme/tokens";
import { SystemSwitch } from "@/ui/Clickable/SystemSwitch";
import { type DropdownItem, SystemDropdown } from "@/ui/Inputs/SystemDropDown";
import { SystemValidationTextField } from "@/ui/Inputs/SystemValidationTextField";
import { InfoIconWithTooltip } from "@/ui/molecules/InfoIconWithTooltip";
import { getAgencySubRoleInfo } from "@/utils/agencySubRoles";
import { type AgencySelectionDraft, resolveAgencySelections } from "@/utils/agencyTargets";
import {
	getRolePriority,
	hppdTargetForDate,
	NurseFullRole,
	NurseFullRoleShort,
	type NurseRole,
	type PostShiftDetails,
	type PostShiftExtraData,
	RecommendationType,
	RequestSource,
	type ShiftType,
} from "@/utils/models";
import { isOpenShiftDurationAllowed } from "@/utils/openShiftDuration";
import { NursePanelHeader } from "@/utils/panelComponents";
import { resolveShiftTimeOptions } from "@/utils/shiftTimeOptions";
import { PanelDayFallback } from "../StaffingChanges/PanelDayFallback";
import { AgencySelectionFields } from "../staffing/AgencySelectionFields";
import { useCheckOverlappingShiftsAndPatch } from "../staffing/hooks/useHandleCheckOverlappingShifts";
import { CreateShiftRow } from "./CreateShiftRow";
import { useGetDepartmentSubRoles } from "./useGetDepartmentSubRoles";

// Sentinel value for the synthetic "Flat Bonus" dropdown option (old free-form bonus flow).
// Distinct from "" because MUI Select treats empty string as "unselected" and won't render its label.
const FLAT_BONUS_VALUE = "flat-bonus";

interface PostShiftRequest {
	agencyDrafts?: AgencySelectionDraft[];
	id: string;
	roleGroups: string[];
	jobId?: string;
	amount: number;
	startTime: DateTime | null;
	endTime: DateTime | null;
	isRecommendation: boolean;
	isChecked: boolean;
	location?: string;
}

// DEFAULT: untouched create rows select no agencies; staff-only is an explicit wire value [].
const rowAgencyDrafts = (row: PostShiftRequest): AgencySelectionDraft[] => row.agencyDrafts ?? [];

const isRequestValid = (request: Omit<PostShiftRequest, "id" | "isRecommendation" | "isChecked">): boolean => {
	return (
		(request.roleGroups.length > 0 || !!request.jobId) &&
		request.amount > 0 &&
		!!request.startTime &&
		request.startTime.isValid &&
		!!request.endTime &&
		request.endTime.isValid &&
		isOpenShiftDurationAllowed(request.startTime, request.endTime)
	);
};

/**
 * Whole calendar days from `from` to `to`, by the date each one shows. The picker and the panel's date
 * can sit in different zones, so comparing instants could land a day off; comparing dates cannot.
 */
const calendarDaysBetween = (from: DateTime, to: DateTime): number =>
	DateTime.utc(to.year, to.month, to.day).diff(DateTime.utc(from.year, from.month, from.day), "days").days;

interface CreateShiftContentProps {
	/** The schedule day the panel opens on; its header can move the post to another day. */
	date: DateTime;
	/** The shift whose default hours new rows start with. */
	shiftType: string;
	isMasterSchedule: boolean;
	onClose?: (success?: boolean) => void;
	role?: `${NurseRole}` | "default";
	onSelectedDateChange?: (date: DateTime) => void;
	defaultLocation?: string;
	defaultJobId?: string;
	defaultRoles?: NurseFullRole[];
	defaultLockedTimes?: { startTime: DateTime; endTime: DateTime };
	hideRecommendations?: boolean;
}

const getRoleGroupsForRoles = (roleGroups: Record<string, string[]>, roles?: NurseFullRole[]): string[] => {
	if (!roles || roles.length === 0) {
		return [];
	}
	const relevantGroups: string[] = [];
	for (const [groupName, groupRoles] of Object.entries(roleGroups)) {
		if (roles.every((role) => groupRoles.includes(NurseFullRoleShort[role]))) {
			relevantGroups.push(groupName);
		}
	}
	return relevantGroups;
};

export const CreateShiftContent: FC<CreateShiftContentProps> = (props) => {
	const schedule = useScheduleWeeks(props.date);
	// The picker's last day comes with the day's weeks; until they arrive the panel can't bound it.
	if (schedule.isLoading || schedule.isError) return <PanelDayFallback panelDay={schedule} dataTestId="create-shift-panel" />;
	return <CreateShiftContentForDay {...props} lastDate={schedule.lastDate} />;
};

const CreateShiftContentForDay: FC<CreateShiftContentProps & { lastDate: DateTime | undefined }> = ({
	date,
	lastDate,
	shiftType,
	isMasterSchedule,
	onClose,
	role = "default",
	onSelectedDateChange,
	defaultLocation,
	defaultJobId,
	defaultRoles,
	defaultLockedTimes,
	hideRecommendations,
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const { isSnf, isAnyDemo, isMedicalDepartment } = useDepartmentFlags();
	const { calculateShiftTimes } = useShiftTimes();
	// The default hours are read by the shift's position in the department's shift types; a panel only opens on a
	// shift type of its department, so the string is one of them.
	const shiftNo = chosenDepartment.shift_types.indexOf(shiftType as ShiftType);
	if (shiftNo === -1) throw new Error(`Post Shift opened on shift type ${shiftType}, which department ${chosenDepartment.department_id} doesn't have`);
	const isPickupByLocation = useFeatureFlag(FEATURE_FLAGS.PickupByLocation);
	const showLocationSelector = isPickupByLocation && !isMedicalDepartment;
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const isShiftBonusesEnabled = useFeatureFlag(FEATURE_FLAGS.EnableShiftBonuses);
	const { track } = useAnalytics();
	const weekView = useOptionalWeekViewContext();
	const { checkForOverlapsAndMergeIfUserApproved } = useCheckOverlappingShiftsAndPatch();
	const { recommendations } = useRecommendationsContext();
	const { t } = useTranslation();
	const [selectedDate, setSelectedDate] = useState<DateTime>(date);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [createMore, setCreateMore] = useState<boolean>(false);
	const [extraPay, setExtraPay] = useState<boolean>(false);
	const [isExtraPayPercentage, setIsExtraPayPercentage] = useState<boolean>(false);
	const [extraPayAmount, setExtraPayAmount] = useState<number>(0);
	const [extraPayString, setExtraPayString] = useState<string>("");
	const [selectedIncentivePlanId, setSelectedIncentivePlanId] = useState<string>(FLAT_BONUS_VALUE);
	const { showSnackbar } = useSnackbar();
	const { data: incentivePlans = [] } = useIncentiveConfigurations(chosenDepartment.department_id);
	const selectedIncentivePlan = useMemo(() => incentivePlans.find((p) => p.id === selectedIncentivePlanId), [incentivePlans, selectedIncentivePlanId]);
	// Synthetic "Flat Bonus" option (default, old free-form flow with an amount input).
	const isSyntheticFlatBonus = selectedIncentivePlanId === FLAT_BONUS_VALUE;
	const isConfiguredPlan = !!selectedIncentivePlan;
	const incentivePlanItems = useMemo<DropdownItem<string>[]>(
		() => [
			{ value: FLAT_BONUS_VALUE, label: t("incentive.plan.flat.bonus"), id: FLAT_BONUS_VALUE },
			...incentivePlans.map((plan) => ({ value: plan.id, label: plan.name, id: plan.id })),
		],
		[incentivePlans, t],
	);
	// Per-window breakdown for the configured-plan chip:
	//   - First row: "Current bonus: $X" (either the first window's value or, if no windows, the plan's baseValue).
	//   - Later rows: "After Nh bonus: $X" for each subsequent window.
	const configuredPlanBreakdown = useMemo<{ label: string; amount: number }[]>(() => {
		if (!selectedIncentivePlan) return [];
		const sorted = [...selectedIncentivePlan.plans].sort((a, b) => a.startHour - b.startHour);
		if (sorted.length === 0) {
			// Flat configured plan — show its base value as the "current bonus".
			return [{ label: t("incentive.current.bonus.label"), amount: selectedIncentivePlan.baseValue }];
		}
		return sorted.map((window) => ({
			label: window.startHour === 0 ? t("incentive.current.bonus.label") : t("incentive.after.hours.bonus.label", { hours: window.startHour }),
			amount: window.value,
		}));
	}, [selectedIncentivePlan, t]);

	const postShiftRecommendation = useMemo(() => {
		return recommendations.find(
			(rec) =>
				rec.type === RecommendationType.PostShift &&
				rec.date.hasSame(selectedDate, "day") &&
				rec.extra_data &&
				Array.isArray((rec.extra_data as PostShiftExtraData).data) &&
				(rec.extra_data as PostShiftExtraData).data.length > 0,
		);
	}, [recommendations, selectedDate]);

	const initialShiftTimes = useMemo(
		() => calculateShiftTimes({ date: selectedDate.startOf("day") }, shiftNo, undefined),
		[calculateShiftTimes, selectedDate, shiftNo],
	);

	const [postShiftRequests, setPostShiftRequests] = useState<PostShiftRequest[]>([]);
	const createEmptyManualEntry = useCallback(
		(isFirst = false): PostShiftRequest => {
			const foundRoleGroups = getRoleGroupsForRoles(chosenDepartment.role_groups, defaultRoles);
			return {
				id: `manual-${uuidv4()}`,
				roleGroups: isFirst && defaultRoles ? foundRoleGroups : [],
				amount: 1,
				startTime: isFirst && defaultLockedTimes ? defaultLockedTimes.startTime : null,
				endTime: isFirst && defaultLockedTimes ? defaultLockedTimes.endTime : null,
				isRecommendation: false,
				// A caller that hides recommendations and prefills who the shift is for (roles, or a job when
				// job management is on) is posting that one slot, so the row starts ticked and Create is ready.
				// Job-section gap clicks carry a job but no roles, so the job alone must be enough to tick it.
				isChecked: isFirst && (!!defaultRoles || (jobManagementEnabled && !!defaultJobId)) && !!hideRecommendations,
				location: isFirst ? defaultLocation : undefined,
				jobId: isFirst && jobManagementEnabled ? defaultJobId : undefined,
			};
		},
		[chosenDepartment.role_groups, defaultRoles, defaultLockedTimes, defaultLocation, defaultJobId, jobManagementEnabled, hideRecommendations],
	);
	const [manualEntries, setManualEntries] = useState<PostShiftRequest[]>(() => [createEmptyManualEntry(true)]);

	const creationAmount = postShiftRequests.filter((r) => r.isChecked).length + manualEntries.filter((e) => e.isChecked).length;
	// A lone manual entry stays content-sized; longer lists share one scroll area beneath the header.
	const showRecommendations = !hideRecommendations && postShiftRequests.length > 0;

	useEffect(() => {
		if (postShiftRecommendation && !hideRecommendations) {
			const recommendationData = (postShiftRecommendation.extra_data as PostShiftExtraData).data;
			const priorityOf = (details: PostShiftDetails): number => {
				const groups = details.recommended_role_groups ?? [];
				return Math.max(0, ...groups.map((group) => getRolePriority(chosenDepartment.role_groups?.[group] ?? [])));
			};
			const sortedData = [...recommendationData].sort((a, b) => {
				const priorityDelta = priorityOf(b) - priorityOf(a);
				if (priorityDelta !== 0) return priorityDelta;
				return DateTime.fromISO(a.recommended_start_time).toMillis() - DateTime.fromISO(b.recommended_start_time).toMillis();
			});
			const requests: PostShiftRequest[] = sortedData.map((data: PostShiftDetails, index) => ({
				id: `recommendation-${index}`,
				roleGroups: data.recommended_role_groups || [],
				jobId: jobManagementEnabled && data.recommended_job_id ? data.recommended_job_id : undefined,
				amount: data.recommended_staff_count || 0,
				startTime: data.recommended_start_time ? DateTime.fromISO(data.recommended_start_time) : initialShiftTimes.startTime,
				endTime: data.recommended_end_time ? DateTime.fromISO(data.recommended_end_time) : initialShiftTimes.endTime,
				isRecommendation: true,
				isChecked: true,
			}));
			setPostShiftRequests(requests);
		} else {
			setPostShiftRequests([]);
		}
	}, [postShiftRecommendation, initialShiftTimes, hideRecommendations, jobManagementEnabled, chosenDepartment.role_groups]);

	const toggleExtraPay = useCallback(() => {
		setExtraPay((prev) => !prev);
	}, []);
	const toggleExtraPayPercentage = useCallback(() => {
		setIsExtraPayPercentage((prev) => !prev);
	}, []);

	const [chargeOnly, setChargeOnly] = useState<boolean>(false);
	const { data: subRoleCatalog = [] } = useGetDepartmentSubRoles(chosenDepartment.department_id);
	const { availableAgencies } = useAgencyAvailability();

	const isRowValid = useCallback(
		(row: PostShiftRequest) => {
			if (!isRequestValid(row)) return false;
			const selections = resolveAgencySelections(rowAgencyDrafts(row));
			if (selections === null) return false;
			return selections.every(
				(selection) =>
					availableAgencies.includes(selection.agency_source) &&
					getAgencySubRoleInfo({
						catalog: subRoleCatalog,
						agencySource: selection.agency_source,
						jobManagementEnabled,
						roleGroups: chosenDepartment.role_groups,
						row,
					}).options.includes(selection.role),
			);
		},
		[availableAgencies, subRoleCatalog, jobManagementEnabled, chosenDepartment.role_groups],
	);
	const isAllValid = useMemo(() => {
		return (
			(postShiftRequests.every((r) => !r.isChecked) || postShiftRequests.every((r) => (r.isChecked ? isRowValid(r) : true))) &&
			manualEntries.every((e) => (e.isChecked ? isRowValid(e) : true))
		);
	}, [postShiftRequests, manualEntries, isRowValid]);
	const allManualEntriesValid = useMemo(() => manualEntries.every((e) => isRowValid(e)), [manualEntries, isRowValid]);

	const expandSearchOptions = useMemo(
		() => [
			{
				key: "charge_only",
				label: t("allow.charge.only"),
				state: chargeOnly,
				setState: setChargeOnly,
			},
		],
		[t, chargeOnly],
	);
	const handleExtraPayAmountChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			let parsedValue = 0;
			if (value === "") {
				setExtraPayAmount(0);
				setExtraPayString("0");
			} else {
				parsedValue = Number.parseFloat(value);
				if (Number.isNaN(parsedValue) || parsedValue < 0) {
					setExtraPayString("0");
					return;
				}
				setExtraPayAmount(Number.parseFloat(parsedValue.toFixed(isExtraPayPercentage ? 0 : 2)));
			}
			setExtraPayString(!isExtraPayPercentage ? value : parsedValue.toFixed(0));
		},
		[isExtraPayPercentage],
	);

	const debouncedExtraPayAmount = useDebouncedValue(extraPayAmount, 700);

	useEffect(() => {
		setExtraPayString(debouncedExtraPayAmount.toFixed(isExtraPayPercentage ? 0 : 2));
	}, [debouncedExtraPayAmount, isExtraPayPercentage]);

	const updateRecommendationRequest = useCallback((id: string, updates: Partial<PostShiftRequest>) => {
		setPostShiftRequests((prev) => prev.map((req) => (req.id === id ? { ...req, ...updates } : req)));
	}, []);

	const updateManualEntry = useCallback((id: string, updates: Partial<Omit<PostShiftRequest, "id" | "isRecommendation">>) => {
		setManualEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, isChecked: true, ...updates } : entry)));
	}, []);

	const addManualEntry = useCallback(() => {
		setManualEntries((prev) => [...prev, createEmptyManualEntry()]);
	}, [createEmptyManualEntry]);

	const removeManualEntry = useCallback((id: string) => {
		setManualEntries((prev) => (prev.length <= 1 ? prev : prev.filter((entry) => entry.id !== id)));
	}, []);

	// A manual row's times are absolute: a gap click locks them to the clicked day, and editing the hours
	// pins them to the day shown then. Moving the panel to another day must carry them along, or the shift
	// still posts on the old day. Rows without times already follow the date through `initialShiftTimes`.
	const handleSelectedDateChange = useCallback(
		(date: DateTime) => {
			const days = calendarDaysBetween(selectedDate, date);
			if (days !== 0) {
				// Adding calendar days (not 24h blocks) keeps each row's wall-clock hours across a DST change.
				setManualEntries((prev) =>
					prev.map((entry) => ({
						...entry,
						startTime: entry.startTime ? entry.startTime.plus({ days }) : null,
						endTime: entry.endTime ? entry.endTime.plus({ days }) : null,
					})),
				);
			}
			setSelectedDate(date);
			onSelectedDateChange?.(date);
		},
		[selectedDate, onSelectedDateChange],
	);

	async function warnShorterShiftPossible(shiftTimes: { startTime: DateTime | null; endTime: DateTime | null }, roleGroups: string[], jobId?: string) {
		const day = { date: selectedDate.startOf("day") };
		// Get first role from first role group for timing calculation
		let roleForTiming = NurseFullRole.RN;
		if (roleGroups.length > 0) {
			const firstRoleGroup = roleGroups[0];
			const roles = chosenDepartment.role_groups[firstRoleGroup];
			if (roles && roles.length > 0) {
				roleForTiming = NurseFullRole[roles[0] as keyof typeof NurseFullRole];
			}
		}
		const defaultShiftTimes = calculateShiftTimes(day, shiftNo, roleForTiming);
		if (!shiftTimes.startTime || !shiftTimes.endTime) {
			return defaultShiftTimes;
		}
		if (defaultShiftTimes.startTime.valueOf() !== shiftTimes.startTime.valueOf() || defaultShiftTimes.endTime.valueOf() !== shiftTimes.endTime.valueOf()) {
			return shiftTimes;
		}

		// Was `partial_block_shifts[role] || []` with no `default` fallback, unlike EditTimePopover —
		// routing through the shared resolver fixes that alongside making it job-aware (DAT-580).
		const { partialBlockShifts: partialShiftHoursOptions } = resolveShiftTimeOptions({ department: chosenDepartment, jobManagementEnabled, jobId, role });

		const targetHours = isSnf ? hppdTargetForDate(chosenDepartment.hppd_target, day.date.weekday) : 4;
		// The day's hours and census as the Week view shows them; opened elsewhere, there is nothing to shorten against.
		const weekViewDay = weekView?.weekViewData.find((weekDay) => weekDay.date.hasSame(selectedDate, "day"));
		if (!weekViewDay) return shiftTimes;
		const shortenShiftHours = partialShiftHoursOptions.find(
			(partialShiftHours: number) => (weekViewDay.dayStats.totalHours + partialShiftHours) / weekViewDay.dayStats.census > targetHours,
		);
		if (!shortenShiftHours) {
			return shiftTimes;
		}

		let changeShiftTimes = false;
		await showConfirmationDialog({
			title: t("shorter.shift.possible"),
			content: t("shorter.shift.question", { hours: shortenShiftHours }),
			actions: [
				{
					label: t("use.recommended.times"),
					onClick: () => {
						changeShiftTimes = true;
						return;
					},
				},
				{
					label: t("keep.existing.times"),
					onClick: () => {
						changeShiftTimes = false;
						return;
					},
				},
			],
		});

		if (changeShiftTimes) {
			return {
				...shiftTimes,
				endTime: shiftTimes.endTime.minus({ hours: shortenShiftHours }),
			};
		}

		return shiftTimes;
	}

	const resetFields = (): void => {
		setManualEntries([createEmptyManualEntry()]);
	};

	const handleCreatePostShift = async () => {
		const selectedRequests: PostShiftRequest[] = [];

		postShiftRequests.forEach((req) => {
			if (req.isChecked) {
				selectedRequests.push(req);
			}
		});

		manualEntries.forEach((entry) => {
			if (entry.isChecked) {
				selectedRequests.push(entry);
			}
		});

		if (selectedRequests.length === 0) {
			showSnackbar(t("error.select.roles.slots"), "error");
			return;
		}

		setIsLoading(true);

		try {
			for (const request of selectedRequests) {
				const agencyTargets = resolveAgencySelections(rowAgencyDrafts(request));
				if (agencyTargets === null) throw new Error(t("agency.multi.selection.invalid"));
				const roleGroups = request.roleGroups;
				const roles = Object.entries(chosenDepartment.role_groups)
					.filter(([key]) => roleGroups.includes(key as NurseFullRole))
					.flatMap(([_, roles]) => roles);
				const uniqueRoles = Array.from(new Set(roles));
				const selectedRoles = uniqueRoles.map<NurseFullRole>((roleShort) => NurseFullRole[roleShort as keyof typeof NurseFullRole]);

				track("Staffing - create open shift", {
					source: isMasterSchedule ? "master_schedule" : "staffing",
					shift_type: shiftType,
					date: date.toFormat("MM/DD/yyyy"),
					roles: JSON.stringify(selectedRoles),
					jobId: request.jobId,
					needed_nurses: request.amount,
					changedFromMasterSchedule: isMasterSchedule,
					isRecommendation: request.isRecommendation,
					location: request.location || undefined,
					agency_targets: JSON.stringify(agencyTargets),
				});

				let shiftTimes = {
					startTime: request.startTime,
					endTime: request.endTime,
				};
				shiftTimes = await warnShorterShiftPossible(shiftTimes, request.roleGroups, request.jobId);
				if (!shiftTimes.startTime || !shiftTimes.endTime) {
					showSnackbar(t("error.invalid.shift.times"), "error");
					throw new Error("Invalid shift times");
				}

				await checkForOverlapsAndMergeIfUserApproved({
					agencyTargets,
					shiftStart: shiftTimes.startTime,
					shiftEnd: shiftTimes.endTime,
					selectedRoles,
					jobId: jobManagementEnabled ? request.jobId : undefined,
					neededNurses: request.amount,
					chargeOnly: chargeOnly,
					bonus: extraPayAmount,
					incentiveConfigurationId: isSyntheticFlatBonus ? undefined : selectedIncentivePlanId,
					incentiveConfigurationPlans: isSyntheticFlatBonus ? undefined : selectedIncentivePlan?.plans,
					isMasterSchedule,
					panel: "create-desktop",
					location: request.location || undefined,
					actionIfMergeCanceled: () => {},
					requestSource: request.isRecommendation ? RequestSource.PostShiftRecommendation : RequestSource.PostShift,
					actionAfterMerge: (result) => {
						track("Staffing - update needed nurses open shift", {
							details: JSON.stringify({
								source: isMasterSchedule ? "master_schedule" : "staffing",
								shift_type: shiftType,
								date: date.toFormat("MM/DD/yyyy"),
								old_needed_nurses: result?.oldNurseAmount ?? 0,
								new_needed_nurses: request.amount,
							}),
						});
					},
					actionsWhenMergeNotNeeded: {
						onSuccess: async () => {},
						onError: () => {},
					},
				});
			}

			// All requests processed
			if (createMore) {
				resetFields();
				setIsLoading(false);
			} else {
				onClose?.(true);
			}
		} catch (error) {
			setIsLoading(false);
			const errorMessage = (error as { message?: unknown } | null)?.message;
			const messageKey = typeof errorMessage === "string" ? errorMessage : null;

			if (messageKey?.startsWith("error.")) {
				// If the error message is a translation key, use it directly.
				showSnackbar(t(messageKey), "error");
			} else {
				// Fallback to the generic error message.
				showSnackbar(t("error.creating.shift"), "error");
			}
		}
	};

	return (
		<Box
			data-testid="post-shift-panel"
			sx={{
				display: "flex",
				flexDirection: "column",
				height: showRecommendations ? STAFF_PICKER.PANEL_HEIGHT : undefined,
				maxHeight: STAFF_PICKER.PANEL_HEIGHT,
				minHeight: 0,
			}}
		>
			<NursePanelHeader
				date={date}
				changeDate={true}
				maxSelectableDate={lastDate}
				onSelectedDateChanged={handleSelectedDateChange}
				title={t("post.shift")}
				allowPastDateSelection={false}
			/>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					flex: 1,
					minHeight: 0,
				}}
			>
				<Box
					data-testid="post-shift-scroll-body"
					sx={{ p: 2, flex: "1 1 auto", minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}
				>
					{availableAgencies.length > 0 && (
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
							<Typography variant="body2" color="text.secondary">
								{t("agency.multi.selection.summary")}
							</Typography>
							<InfoIconWithTooltip title={t("agency.multi.selection.hint")} ariaLabel={t("agency.multi.details")} dataTestId="post-shift-agency-help" />
						</Box>
					)}
					{showRecommendations && (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 2,
								flexShrink: 0,
							}}
						>
							<Typography variant="subtitle2" color="text.primary">
								{t("recommendations")}
							</Typography>
							{postShiftRequests.map((request) => (
								<Box
									key={request.id}
									data-testid={`post-shift-entry-${request.id}`}
									sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, display: "flex", flexDirection: "column", gap: 2 }}
								>
									<CreateShiftRow
										responsive
										roleGroups={request.roleGroups}
										setRoleGroups={(roleGroups) => {
											if (Array.isArray(roleGroups)) {
												updateRecommendationRequest(request.id, {
													roleGroups,
												});
											}
										}}
										{...(jobManagementEnabled
											? {
													jobId: request.jobId,
													setJobId: (jobId: string) =>
														updateRecommendationRequest(request.id, {
															jobId,
														}),
												}
											: {})}
										amount={request.amount}
										setAmount={(amount) => {
											if (amount !== undefined) {
												updateRecommendationRequest(request.id, { amount });
											}
										}}
										times={{ startTime: request.startTime, endTime: request.endTime }}
										updateTimes={(startTime, endTime) => updateRecommendationRequest(request.id, { startTime, endTime })}
										isChecked={request.isChecked}
										setIsChecked={(isChecked) => updateRecommendationRequest(request.id, { isChecked })}
										isValid={isRowValid(request)}
									/>
									{request.isChecked && availableAgencies.length > 0 && (
										<AgencySelectionFields
											compact
											mode="create"
											agencies={availableAgencies}
											value={rowAgencyDrafts(request)}
											onChange={(agencyDrafts) => updateRecommendationRequest(request.id, { agencyDrafts })}
											jobId={request.jobId}
											roleGroups={request.roleGroups}
											jobManagementEnabled={jobManagementEnabled}
											dataTestId={`create-agencies-${request.id}`}
										/>
									)}
								</Box>
							))}
						</Box>
					)}
					{showRecommendations && <Divider />}
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 2,
							flexShrink: 0,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center" }}>
							<Typography variant="subtitle2" color="text.primary">
								{t("manual.entry")}
							</Typography>
						</Box>
						{manualEntries.map((entry, index) => (
							<Box
								key={entry.id}
								data-testid={`post-shift-entry-${entry.id}`}
								sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, display: "flex", flexDirection: "column", gap: 2 }}
							>
								<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
									<CreateShiftRow
										responsive
										// Manual entry only, and only the first: the recommendations list above is empty
										// unless the day has a live recommendation, but there is always one manual row.
										data-annie-target={index === 0 ? "post-shift-entry-row" : undefined}
										roleGroups={entry.roleGroups}
										setRoleGroups={(roleGroups) => {
											if (Array.isArray(roleGroups)) {
												updateManualEntry(entry.id, {
													roleGroups,
												});
											}
										}}
										{...(jobManagementEnabled
											? {
													jobId: entry.jobId,
													setJobId: (jobId: string) => updateManualEntry(entry.id, { jobId }),
												}
											: {})}
										amount={entry.amount}
										setAmount={(amount) => {
											if (amount !== undefined) {
												updateManualEntry(entry.id, { amount });
											}
										}}
										times={{ startTime: entry.startTime, endTime: entry.endTime }}
										fallbackTimes={initialShiftTimes}
										updateTimes={(startTime, endTime) => updateManualEntry(entry.id, { startTime, endTime })}
										isChecked={entry.isChecked}
										setIsChecked={(isChecked) => updateManualEntry(entry.id, { isChecked })}
										isValid={isRowValid(entry)}
										location={entry.location}
										setLocation={showLocationSelector ? (location) => updateManualEntry(entry.id, { location }) : undefined}
										locationParDay={showLocationSelector ? { date: selectedDate, shiftType, isActual: false } : undefined}
									/>
									{manualEntries.length > 1 && (
										<IconButton size="small" onClick={() => removeManualEntry(entry.id)} sx={{ ml: 1, color: "#EF4444" }}>
											<RemoveCircleOutlineIcon fontSize="small" />
										</IconButton>
									)}
									{index === manualEntries.length - 1 && (
										<Tooltip title={!allManualEntriesValid ? t("manual.entry.invalid.fields") : ""}>
											<Box>
												<IconButton
													size="small"
													data-testid="create-shift-add-manual-entry-button"
													onClick={addManualEntry}
													disabled={!allManualEntriesValid}
													sx={{ ml: 1, color: "primary.main", "&.Mui-disabled": { color: "#E1E6EF" } }}
												>
													<AddCircleOutlineIcon fontSize="small" />
												</IconButton>
											</Box>
										</Tooltip>
									)}
								</Box>
								{entry.isChecked && availableAgencies.length > 0 && (
									<AgencySelectionFields
										compact
										mode="create"
										agencies={availableAgencies}
										value={rowAgencyDrafts(entry)}
										onChange={(agencyDrafts) => updateManualEntry(entry.id, { agencyDrafts })}
										jobId={entry.jobId}
										roleGroups={entry.roleGroups}
										jobManagementEnabled={jobManagementEnabled}
										dataTestId={`create-agencies-${entry.id}`}
									/>
								)}
							</Box>
						))}
					</Box>
				</Box>

				{/* Action Buttons */}
				<Box
					data-testid="post-shift-footer"
					sx={{
						display: "flex",
						justifyContent: "space-between",
						gap: 2,
						flexWrap: "wrap",
						bgcolor: "background.paper",
						p: 2,
						flexShrink: 0,
						borderTop: 1,
						borderColor: "divider",
					}}
				>
					<Box>
						<FormGroup
							data-annie-target="post-shift-options"
							sx={{
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
								gap: { xs: 1, sm: 4 },
								mt: 0.5,
								flexWrap: "wrap",
							}}
						>
							{expandSearchOptions
								.filter(({ key }) => key !== "post_to_nursa")
								.map(({ key, label, state, setState }) => (
									<FormControlLabel
										key={key}
										data-testid={`post-shift-${key}-toggle`}
										sx={{ m: 0 }}
										control={
											<Checkbox
												checked={state}
												onChange={(e) => setState(e.target.checked)}
												sx={{
													color: "primary.main",
													"&.Mui-checked": { color: "primary.main" },
												}}
											/>
										}
										label={label}
									/>
								))}
							{(isShiftBonusesEnabled || isAnyDemo) && (
								<>
									<FormControlLabel
										data-testid="post-shift-extra-pay-toggle"
										sx={{ m: 0 }}
										control={
											<Checkbox
												checked={extraPay}
												onChange={toggleExtraPay}
												sx={{
													color: "primary.main",
													"&.Mui-checked": { color: "primary.main" },
												}}
											/>
										}
										label={t("incentive.pay")}
									/>
									{extraPay && (
										<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2, ml: 4 }}>
											<Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 2 }}>
												{isAnyDemo && (
													<SystemSwitch label={t("by.percentage")} disabled={!extraPay} checked={isExtraPayPercentage} onChange={toggleExtraPayPercentage} />
												)}
												<SystemDropdown
													label={t("incentive.plan")}
													items={incentivePlanItems}
													selectedItem={selectedIncentivePlanId}
													onChange={(value) => setSelectedIncentivePlanId(value ?? FLAT_BONUS_VALUE)}
													formControlSx={{ minWidth: 200 }}
													data-testid="post-shift-incentive-plan"
												/>
												{isSyntheticFlatBonus && (
													<SystemValidationTextField
														disabled={!extraPay}
														variant={"outlined"}
														reserveHeightForHelperText={false}
														label={isExtraPayPercentage ? t("percentage") : t("amount")}
														placeholder={isExtraPayPercentage ? "%" : "$"}
														inputType={"number"}
														value={extraPayString}
														onChange={handleExtraPayAmountChange}
														maxValue={1000}
														inputProps={{
															endAdornment: (
																<InputAdornment position={"end"}>
																	{isExtraPayPercentage ? <PercentOutlinedIcon fontSize={"small"} /> : <AttachMoneyOutlinedIcon fontSize={"small"} />}
																</InputAdornment>
															),
														}}
													/>
												)}
												{isConfiguredPlan && configuredPlanBreakdown.length > 0 && (
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 1,
															px: 1.5,
															py: 0.75,
															bgcolor: "background.paper",
															border: "1px solid",
															borderColor: "divider",
															borderRadius: 1,
														}}
													>
														<InfoIconWithTooltip title={t("incentive.fast.grab.chip.tooltip")} />
														<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
															{configuredPlanBreakdown.map((row) => (
																<Typography key={row.label} sx={{ fontSize: "0.75rem" }}>
																	<strong>{row.label}:</strong>&nbsp;${row.amount.toFixed(2)}
																</Typography>
															))}
														</Box>
													</Box>
												)}
											</Box>
										</Box>
									)}
								</>
							)}
							<FormControlLabel
								data-testid="post-shift-create-more-toggle"
								sx={{ m: 0 }}
								control={
									<Checkbox
										checked={createMore}
										onChange={(e) => setCreateMore(e.target.checked)}
										sx={{
											color: "primary.main",
											"&.Mui-checked": { color: "primary.main" },
										}}
									/>
								}
								label={t("create.more")}
							/>
						</FormGroup>
					</Box>
					<Box sx={{ display: "flex", gap: 3 }}>
						<Button
							data-testid={"post-shift-cancel-button"}
							variant="outlined"
							onClick={() => onClose?.(false)}
							sx={{
								textTransform: "none",
								borderColor: "#E1E6EF",
								color: "primary.dark",
								borderRadius: "8px",
								px: 3,
								height: "40px",
								"&:hover": {
									borderColor: "primary.main",
									bgcolor: "transparent",
								},
							}}
						>
							{t("cancel")}
						</Button>
						<Button
							data-testid={"post-shift-create-button"}
							data-annie-target="post-shift-submit"
							variant="contained"
							onClick={handleCreatePostShift}
							disabled={!isAllValid || isLoading || creationAmount === 0}
							sx={{
								textTransform: "none",
								bgcolor: "primary.main",
								borderRadius: "8px",
								color: "white",
								height: "40px",
								"&:hover": {
									bgcolor: "primary.dark",
								},
								px: 3,
								"&.Mui-disabled": {
									bgcolor: "#E1E6EF",
									color: "primary.main",
								},
							}}
						>
							{isLoading ? t("creating") : `${creationAmount} ${t("create")}`}
						</Button>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export function showConfirmationDialog({
	title,
	content,
	actions,
}: {
	title: string;
	content: string;
	actions: { label: string; onClick: () => void }[];
}): Promise<void> {
	return new Promise((resolve) => {
		const dialog = document.createElement("div");
		document.body.appendChild(dialog);

		const root = createRoot(dialog);
		root.render(
			<Dialog
				data-testid={`confirmation-dialog-${title}`}
				open={true}
				sx={{
					"& .MuiDialog-paper": {
						borderRadius: "12px",
						maxWidth: "550px",
						width: "100%",
					},
				}}
			>
				<DialogTitle
					sx={{
						fontSize: "18px",
						fontWeight: 600,
						color: "#1A1A1A",
						pt: 3,
						px: 3,
						textAlign: "center",
					}}
				>
					{title}
				</DialogTitle>
				<DialogContent sx={{ px: 3 }}>
					<DialogContentText
						sx={{
							color: "text.secondary",
							textAlign: "center",
						}}
					>
						{content}
					</DialogContentText>
				</DialogContent>
				<DialogActions
					sx={{
						p: 3,
						display: "flex",
						justifyContent: "center",
						gap: 2,
						"& .MuiButton-root": {
							flex: 1,
							maxWidth: "250px",
						},
					}}
				>
					{actions.map((action, index) => (
						<Button
							data-testid={`confirmation-dialog-${title}-${action.label}`}
							key={index}
							onClick={() => {
								action.onClick();
								root.unmount();
								document.body.removeChild(dialog);
								resolve();
							}}
							variant={index === actions.length - 1 ? "contained" : "outlined"}
							sx={{
								textTransform: "none",
								borderRadius: "8px",
								px: 3,
								height: "40px",
								whiteSpace: "nowrap",
								...(index === actions.length - 1
									? {
											bgcolor: "primary.main",
											color: "white",
											"&:hover": {
												bgcolor: "primary.dark",
											},
										}
									: {
											borderColor: "#E1E6EF",
											color: "primary.dark",
											"&:hover": {
												borderColor: "primary.main",
												bgcolor: "transparent",
											},
										}),
							}}
						>
							{action.label}
						</Button>
					))}
				</DialogActions>
			</Dialog>,
		);
	});
}
